import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { SaRaService } from 'src/app/services/sa-ra.service';
import * as SaRaActions from './sa-ra.actions';
import { select, Store } from '@ngrx/store';
import { updateStatusReset } from '../status/status.actions';
import { StatusType } from 'src/app/core/enum/status.enum';

@Injectable()
export class SaRaEffects {
  loadRaTemplate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.loadRaTemplate),
      switchMap((action) => {
        return this.saRaService.getRaTemplate(action.id).pipe(
          map((raTemplates) => {
            return SaRaActions.loadRaTemplateSuccess({
              raTemplate: raTemplates.body,
            });
          })
        );
      })
    );
  });

  loadLikelihood$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.loadLikelihood),
      switchMap((action) => {
        return this.saRaService.getLikelihood(action.rtId).pipe(
          map((response) => {
            return SaRaActions.loadLikelihoodSuccess({
              likelihood: response.body,
            });
          })
        );
        return of(SaRaActions.dummyAction());
      })
    );
  });

  addLikelihood$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.addLikelihood),
      switchMap((action) => {
        return this.saRaService.addLikeliHood(action.likelihood).pipe(
          map((response) => {
            updateStatusReset(StatusType.Created, this.store);
            return SaRaActions.addLikelihoodSuccess({
              likelihood: response,
            });
          })
        );
      })
    );
  });

  updateLikelihood$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.updateLikelihood),
      switchMap((action) => {
        const { likelihood: lk } = action;
        if (!lk) return of(SaRaActions.dummyAction());
        return this.saRaService.updateLikeliHood(lk.id, lk).pipe(
          map((ulk) => {
            if (!ulk) return SaRaActions.dummyAction();
            updateStatusReset(StatusType.Updated, this.store);
            return SaRaActions.updateLikelihoodSuccess({
              likelihood: ulk,
            });
          })
        );
      })
    );
  });
  deleteLikelihood$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.deleteLikelihood),
      switchMap((action) => {
        const { likelihood } = action;
        if (!likelihood) return of(SaRaActions.dummyAction());

        return this.saRaService.deleteLikeliHood(likelihood.id).pipe(
          map(() => {
            updateStatusReset(StatusType.Deleted, this.store);
            return SaRaActions.deleteLikelihoodSuccess({ likelihood });
          })
        );
      })
    );
  });

  loadImpactRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.loadImpactRating),
      switchMap((action) => {
        return this.saRaService.getImpactRating(action.rtId).pipe(
          map((response) => {
            return SaRaActions.loadImpactRatingSuccess({
              impactRating: response.body,
            });
          })
        );
        return of(SaRaActions.dummyAction());
      })
    );
  });

  addImpactRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.addImpactRating),
      switchMap((action) => {
        return this.saRaService.addImpactRating(action.impactRating).pipe(
          map((response) => {
            updateStatusReset(StatusType.Created, this.store);
            return SaRaActions.addImpactRatingSuccess({
              impactRating: response,
            });
          })
        );
      })
    );
  });

  updateImpactRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.updateImpactRating),
      switchMap((action) => {
        const { impactRating: ir } = action;
        if (!ir) return of(SaRaActions.dummyAction());
        return this.saRaService.updateImpactRating(ir.id, ir).pipe(
          map((uir) => {
            if (!uir) return SaRaActions.dummyAction();
            updateStatusReset(StatusType.Updated, this.store);
            return SaRaActions.updateImpactRatingSuccess({
              impactRating: uir,
            });
          })
        );
      })
    );
  });

  deleteImpactRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.deleteImpactRating),
      switchMap((action) => {
        const { impactRating } = action;
        if (!impactRating) return of(SaRaActions.dummyAction());

        return this.saRaService.deleteImpactRating(impactRating.id).pipe(
          map(() => {
            updateStatusReset(StatusType.Deleted, this.store);
            return SaRaActions.deleteImpactRatingSuccess({ impactRating });
          })
        );
      })
    );
  });

  // risk rating

  loadRiskRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.loadRiskRating),
      switchMap((action) => {
        return this.saRaService.getRiskRating(action.rtId).pipe(
          map((response) => {
            return SaRaActions.loadRiskRatingSuccess({
              riskRating: response.body,
            });
          })
        );
        return of(SaRaActions.dummyAction());
      })
    );
  });

  addRiskRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.addRiskRating),
      switchMap((action) => {
        return this.saRaService.addRiskRating(action.riskRating).pipe(
          map((response) => {
            updateStatusReset(StatusType.Created, this.store);
            return SaRaActions.addRiskRatingSuccess({
              riskRating: response,
            });
          })
        );
      })
    );
  });

  updateRiskRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.updateRiskRating),
      switchMap((action) => {
        const { riskRating: rr } = action;
        if (!rr) return of(SaRaActions.dummyAction());
        return this.saRaService.updateRiskRating(rr.id, rr).pipe(
          map((urr) => {
            if (!urr) return SaRaActions.dummyAction();
            updateStatusReset(StatusType.Updated, this.store);
            return SaRaActions.updateRiskRatingSuccess({
              riskRating: urr,
            });
          })
        );
      })
    );
  });
  deleteRiskRating$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.deleteRiskRating),
      switchMap((action) => {
        const { riskRating } = action;
        if (!riskRating) return of(SaRaActions.dummyAction());

        return this.saRaService.deleteRiskRating(riskRating.id).pipe(
          map(() => {
            updateStatusReset(StatusType.Deleted, this.store);
            return SaRaActions.deleteRiskRatingSuccess({ riskRating });
          })
        );
      })
    );
  });

  // Vulnerabilities

  loadVulnerabilities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.loadVulnerabilities),
      switchMap((action) => {
        return this.saRaService.getVulnerabilities(action.rtId).pipe(
          map((response) => {
            return SaRaActions.loadVulnerabilitiesSuccess({
              vulnerabilities: response.body,
            });
          })
        );
        return of(SaRaActions.dummyAction());
      })
    );
  });

  addVulnerabilities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.addVulnerabilities),
      switchMap((action) => {
        return this.saRaService.addVulnerabilities(action.vulnerabilities).pipe(
          map((response) => {
            updateStatusReset(StatusType.Created, this.store);
            return SaRaActions.addVulnerabilitiesSuccess({
              vulnerabilities: response,
            });
          })
        );
      })
    );
  });

  updateVulnerabilities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.updateVulnerabilities),
      switchMap((action) => {
        const { vulnerabilities: vd } = action;
        if (!vd) return of(SaRaActions.dummyAction());
        return this.saRaService.updateVulnerabilities(vd.id, vd).pipe(
          map((uvd) => {
            if (!uvd) return SaRaActions.dummyAction();
            updateStatusReset(StatusType.Updated, this.store);
            return SaRaActions.updateVulnerabilitiesSuccess({
              vulnerabilities: uvd,
            });
          })
        );
      })
    );
  });

  deleteVulnerabilities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.deleteVulnerabilities),
      switchMap((action) => {
        const { vulnerabilities } = action;
        if (!vulnerabilities) return of(SaRaActions.dummyAction());

        return this.saRaService.deleteVulnerabilities(vulnerabilities.id).pipe(
          map(() => {
            updateStatusReset(StatusType.Deleted, this.store);
            return SaRaActions.deleteVulnerabilitiesSuccess({
              vulnerabilities,
            });
          })
        );
      })
    );
  });

  // Risk Velocity

  loadRiskVelocity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.loadRiskVelocity),
      switchMap((action) => {
        return this.saRaService.getRiskVelocity(action.rtId).pipe(
          map((response) => {
            return SaRaActions.loadRiskVelocitySuccess({
              riskVelocity: response.body,
            });
          })
        );
        return of(SaRaActions.dummyAction());
      })
    );
  });

  addRiskVelocity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.addRiskVelocity),
      switchMap((action) => {
        return this.saRaService.addRiskVelocity(action.riskVelocity).pipe(
          map((response) => {
            updateStatusReset(StatusType.Created, this.store);
            return SaRaActions.addRiskVelocitySuccess({
              riskVelocity: response,
            });
          })
        );
      })
    );
  });

  updateRiskVelocity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.updateRiskVelocity),
      switchMap((action) => {
        const { riskVelocity: rv } = action;
        if (!rv) return of(SaRaActions.dummyAction());
        return this.saRaService.updateRiskVelocity(rv.id, rv).pipe(
          map((urv) => {
            if (!urv) return SaRaActions.dummyAction();
            updateStatusReset(StatusType.Updated, this.store);
            return SaRaActions.updateRiskVelocitySuccess({
              riskVelocity: urv,
            });
          })
        );
      })
    );
  });
  deleteRiskVelocity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaRaActions.deleteRiskVelocity),
      switchMap((action) => {
        const { riskVelocity } = action;
        if (!riskVelocity) return of(SaRaActions.dummyAction());

        return this.saRaService.deleteRiskVelocity(riskVelocity.id).pipe(
          map(() => {
            updateStatusReset(StatusType.Deleted, this.store);
            return SaRaActions.deleteRiskVelocitySuccess({ riskVelocity });
          })
        );
      })
    );
  });
  constructor(
    private actions$: Actions,
    private saRaService: SaRaService,
    private store: Store
  ) {}
}
